import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Stack } from '@mui/material';
import { memo } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { Tooltip } from 'src/components';
import { FormTextField } from 'src/components/Forms';
import { AccordionSummaryActionIconButton } from 'src/pages/Settings/memberships/components/plans-tab/accordion-summary-action-icon-button';
import {
  PlanAccordionDetails,
  PlanAccordionSummary,
  PlanAccordionWrapper,
  PlanName,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  type PlansTabFaqQuestionValues,
  PlansTabField,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

type FaqItemProps = {
  index: number;
  faqId: string;
  expanded: boolean;
  onExpandedChange: (faqId: string, expanded: boolean) => void;
  onRemove: (index: number, faqId: string) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  moveUpToIndex?: number;
  moveDownToIndex?: number;
  readOnly: boolean;
};

type FaqFieldPath = FieldPath<PlansTabFormValues>;

const getFaqFieldName = (index: number, field: 'question' | 'answer'): FaqFieldPath =>
  `${PlansTabField.OptionsFaqQuestions}.${index}.${field}` as FaqFieldPath;

export const FaqItem = ({
  index,
  faqId,
  expanded,
  onExpandedChange,
  onRemove,
  onMove,
  moveUpToIndex,
  moveDownToIndex,
  readOnly,
}: FaqItemProps) => {
  const { control } = useFormContext<PlansTabFormValues>();

  const question =
    (useWatch({
      control,
      name: getFaqFieldName(index, 'question'),
    }) as PlansTabFaqQuestionValues['question'] | undefined) ?? '';

  const questionTitle = question.trim() || 'FAQ';

  return (
    <PlanAccordionWrapper
      elevation={0}
      disableGutters
      expanded={expanded}
      onChange={(_, expandedValue) => {
        onExpandedChange(faqId, expandedValue);
      }}
      data-testid={`faq-item-${faqId}`}
    >
      <PlanAccordionSummary expandIcon={<ChevronRightIcon />}>
        <Tooltip title={questionTitle}>
          <PlanName variant="body2">{questionTitle}</PlanName>
        </Tooltip>
        {!readOnly ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AccordionSummaryActionIconButton
              aria-label="Move FAQ up"
              disabled={typeof moveUpToIndex !== 'number'}
              onAction={() => onMove(index, moveUpToIndex!)}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Move FAQ down"
              disabled={typeof moveDownToIndex !== 'number'}
              onAction={() => onMove(index, moveDownToIndex!)}
            >
              <ArrowDownwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Delete FAQ"
              onAction={() => onRemove(index, faqId)}
            >
              <DeleteOutlineIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
          </Stack>
        ) : null}
      </PlanAccordionSummary>

      {expanded ? (
        <PlanAccordionDetails
          sx={{
            borderTop: 'none',
          }}
        >
          <Stack gap={2} width="100%">
            <FormTextField
              type="textfield"
              name={getFaqFieldName(index, 'question')}
              label="Question"
              required="Question is required"
              showErrorText
              disabled={readOnly}
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                multiline: true,
                minRows: 2,
              }}
            />

            <FormTextField
              type="textfield"
              name={getFaqFieldName(index, 'answer')}
              label="Answer"
              required="Answer is required"
              showErrorText
              disabled={readOnly}
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                multiline: true,
                minRows: 4,
              }}
            />
          </Stack>
        </PlanAccordionDetails>
      ) : null}
    </PlanAccordionWrapper>
  );
};

export const MemoFaqItem = memo(FaqItem);
